import './MaintenancePage.scss';
import { useTranslation } from 'react-i18next';
import maintenanceLogo from '../../assets/images/logo.svg';
import { getMaintenanceConfig } from '../../helpers/maintenanceHelper';

const MaintenancePage = () => {
  const { t } = useTranslation();
  const maintenanceConfig = getMaintenanceConfig();

  return (
    <div className="maintenance-page">
      <div className="maintenance-page__container">
        <div className="maintenance-page__header">
          <img
            src={maintenanceLogo}
            alt="Air Canada Logo"
            className="maintenance-page__logo"
          />
        </div>

        <div className="maintenance-page__content">
          <div className="maintenance-page__icon-container">
            <div className="maintenance-page__icon">
              <svg
                width="120"
                height="80"
                viewBox="0 0 120 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="airplane-maintenance-icon"
              >
                {/* Background sky with subtle gradient */}
                <rect
                  width="120"
                  height="80"
                  fill="url(#skyGradient)"
                  rx="10"
                />

                {/* Gradient definitions */}
                <defs>
                  <linearGradient
                    id="skyGradient"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#f0f8ff" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#e1f0ff" stopOpacity="0.1" />
                  </linearGradient>
                  <linearGradient
                    id="planeGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#005078" />
                    <stop offset="100%" stopColor="#0066a0" />
                  </linearGradient>
                </defs>

                {/* Airplane body */}
                <g className="airplane-flying">
                  {/* Main fuselage */}
                  <ellipse
                    cx="60"
                    cy="35"
                    rx="25"
                    ry="3"
                    fill="url(#planeGradient)"
                  />

                  {/* Nose cone */}
                  <path
                    d="M85 35 L90 35 Q92 35 90 37 Q92 35 90 33 Z"
                    fill="#005078"
                  />

                  {/* Main wings */}
                  <ellipse
                    cx="55"
                    cy="35"
                    rx="6"
                    ry="15"
                    fill="#005078"
                    opacity="0.9"
                  />

                  {/* Wing tips */}
                  <ellipse cx="55" cy="48" rx="2" ry="3" fill="#005078" />
                  <ellipse cx="55" cy="22" rx="2" ry="3" fill="#005078" />

                  {/* Tail section */}
                  <ellipse cx="38" cy="35" rx="3" ry="2" fill="#005078" />

                  {/* Vertical tail */}
                  <path
                    d="M38 35 L32 28 L36 35 L32 42 L38 35 Z"
                    fill="#005078"
                  />

                  {/* Horizontal stabilizer */}
                  <ellipse
                    cx="35"
                    cy="35"
                    rx="2"
                    ry="6"
                    fill="#005078"
                    opacity="0.8"
                  />

                  {/* Air Canada maple leaf on tail */}
                  <path
                    d="M35 35 L36 32 L37.5 33 L36.5 30.5 L38.5 31 L37 28.5 L39 29.5 L37.5 27 L35 30 L32.5 27 L31 29.5 L33 28.5 L31.5 31 L33.5 30.5 L32.5 33 L34 32 Z"
                    fill="#d8292f"
                    opacity="0.9"
                  />

                  {/* Cockpit windows */}
                  <ellipse cx="78" cy="35" r="2" fill="#87ceeb" opacity="0.8" />
                  <ellipse
                    cx="82"
                    cy="35"
                    r="1.5"
                    fill="#87ceeb"
                    opacity="0.8"
                  />

                  {/* Passenger windows */}
                  <circle
                    cx="72"
                    cy="35"
                    r="0.8"
                    fill="#87ceeb"
                    opacity="0.7"
                  />
                  <circle
                    cx="69"
                    cy="35"
                    r="0.8"
                    fill="#87ceeb"
                    opacity="0.7"
                  />
                  <circle
                    cx="66"
                    cy="35"
                    r="0.8"
                    fill="#87ceeb"
                    opacity="0.7"
                  />
                  <circle
                    cx="63"
                    cy="35"
                    r="0.8"
                    fill="#87ceeb"
                    opacity="0.7"
                  />
                  <circle
                    cx="60"
                    cy="35"
                    r="0.8"
                    fill="#87ceeb"
                    opacity="0.7"
                  />
                  <circle
                    cx="57"
                    cy="35"
                    r="0.8"
                    fill="#87ceeb"
                    opacity="0.7"
                  />

                  {/* Engines under wings */}
                  <ellipse cx="53" cy="40" rx="3" ry="1.5" fill="#737373" />
                  <ellipse cx="53" cy="30" rx="3" ry="1.5" fill="#737373" />

                  {/* Engine intakes */}
                  <circle cx="56" cy="40" r="1" fill="#333333" />
                  <circle cx="56" cy="30" r="1" fill="#333333" />
                </g>

                {/* Flight path/trail */}
                <path
                  d="M20 45 Q40 42 60 45 Q80 48 100 45"
                  stroke="#005078"
                  strokeWidth="1"
                  strokeDasharray="3,2"
                  fill="none"
                  opacity="0.3"
                  className="flight-path"
                />

                {/* Cloud elements */}
                <circle cx="25" cy="25" r="3" fill="#ffffff" opacity="0.6" />
                <circle cx="28" cy="25" r="4" fill="#ffffff" opacity="0.6" />
                <circle cx="32" cy="25" r="3" fill="#ffffff" opacity="0.6" />

                <circle cx="95" cy="20" r="2.5" fill="#ffffff" opacity="0.5" />
                <circle cx="97" cy="20" r="3.5" fill="#ffffff" opacity="0.5" />
                <circle cx="100" cy="20" r="2.5" fill="#ffffff" opacity="0.5" />

                {/* Maintenance loading bar */}
                <rect
                  x="30"
                  y="60"
                  width="60"
                  height="4"
                  rx="2"
                  fill="#e6e6e6"
                />
                <rect
                  x="30"
                  y="60"
                  width="24"
                  height="4"
                  rx="2"
                  fill="#009370"
                  className="maintenance-progress"
                />

                {/* Loading text */}
                <text
                  x="60"
                  y="72"
                  textAnchor="middle"
                  fontSize="8"
                  fill="#005078"
                  opacity="0.7"
                  fontFamily="Arial, sans-serif"
                >
                  Maintenance in Progress...
                </text>

                {/* Animated status dots */}
                <circle
                  cx="20"
                  cy="65"
                  r="1.5"
                  fill="#d8292f"
                  className="status-dot-1"
                />
                <circle
                  cx="100"
                  cy="65"
                  r="1.5"
                  fill="#009370"
                  className="status-dot-2"
                />
              </svg>
            </div>
          </div>

          <div className="maintenance-page__message">
            <h1 className="maintenance-page__title">
              {t('maintenance.title', 'System Maintenance')}
            </h1>
            <p className="maintenance-page__description">
              {maintenanceConfig.messageOverride ||
                t('maintenance.description')}
            </p>
          </div>
        </div>

        <div className="maintenance-page__footer">
          <p className="maintenance-page__footer-text">
            {t('maintenance.footer', 'Thank you for your patience')}
          </p>
          {maintenanceConfig.contactEmail && (
            <p className="maintenance-page__contact">
              {t('maintenance.contactPrefix', 'For urgent matters, contact: ')}
              <a
                href={`mailto:${maintenanceConfig.contactEmail}`}
                className="maintenance-page__contact-link"
              >
                {maintenanceConfig.contactEmail}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
